/**
 * Star toggle for one person on the assignee list: "new tasks are mine unless I
 * say otherwise".
 *
 * Per viewer, not per app -- the star you see is your own, and starring somebody
 * says nothing about whose default they are for anyone else. That is also why
 * the label says "my" out loud rather than "the default assignee".
 *
 * Deliberately shaped like PinButton, down to swallowing the click: the list row
 * wraps its name in a link that covers the whole row (`after:inset-0`), so
 * without this the star would also navigate to the editor.
 */
export function StarButton({
  starred,
  busy = false,
  onClick,
  className = '',
}: {
  starred: boolean;
  busy?: boolean;
  onClick: () => void;
  className?: string;
}) {
  const label = starred ? 'Not my default assignee' : 'Make my default assignee';

  return (
    <button
      type="button"
      disabled={busy}
      aria-pressed={starred}
      aria-label={label}
      title={
        starred
          ? 'New tasks you create are assigned to them. Click to stop.'
          : 'Assign new tasks you create to them by default'
      }
      onClick={(event) => {
        // The row's link sits under the whole row, so both have to be stopped:
        // preventDefault for the anchor, stopPropagation for anything listening
        // further up.
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      // `relative` lifts it above the link overlay -- a positioned pseudo-element
      // spanning the row would otherwise take the click before the button does.
      //
      // Unstarred, this used to sit at `text-slate-300` -- about 2:1 on a white
      // row, so every row carried a faint grey star of no obvious purpose that
      // was also too pale to read as a control. It is now hidden until the row
      // is in play and legible once it is. `group-focus-within` rather than
      // hover alone keeps it reachable from the keyboard, and the starred one is
      // always visible because that one is state, not an affordance.
      className={`relative rounded-md p-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
        starred
          ? 'text-honey-500 hover:text-honey-600'
          : 'text-stone-400 opacity-0 hover:text-stone-700 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100'
      } ${className}`}
    >
      <StarIcon filled={starred} />
    </button>
  );
}

/* Hand-drawn to match the other glyphs, exactly as PinIcon is: there is no icon
   package, and the repo has decided not to add one for a handful of shapes. */
function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
    >
      <path d="M12 4.5l2.32 4.7 5.18.75-3.75 3.66.885 5.16L12 16.33l-4.635 2.44.885-5.16L4.5 9.95l5.18-.75L12 4.5Z" />
    </svg>
  );
}
